import classNames from 'classnames';
import { Tab, Tabs } from '@mui/material';
import { renderToString } from 'react-dom/server';
import { useCallback, useEffect, useState } from 'react';
import { uuid } from '@loyalty/shared-common';
import { GenericComponent } from '../../components-tree';
import { ReactBootstrap } from '../../ReactBootstrap';
import { WidgetProps } from '../render-component';

export interface TabsComponentAttributes {
  type: 'tabs' | 'navigations';
  tabs: {
    contentId: string | null;
  }[];
}

export interface TabsComponentI18nAttributes {
  tabLabels: Record<string, string>;
}

type TabsComponent = GenericComponent<TabsComponentAttributes, TabsComponentI18nAttributes>;

type TabsWidgetBackendProps = WidgetProps<TabsComponent>;

interface TabsWidgetFrontendProps {
  id: string;
  type: 'tabs' | 'navigations';
  className?: string;
  attributes: TabsWidgetBackendProps['options']['attributes'];
  localizedTabs: {
    contentId: string;
    label: string;
  }[];
}

/**
 * Gets the active tab index from the URL hash for navigation components.
 * Returns the index if found, otherwise returns 0.
 */
function getActiveTabIndexFromHash(
  localizedTabs: TabsWidgetFrontendProps['localizedTabs'],
): number {
  if (typeof window === 'undefined') return 0;
  const hash = window.location.hash.slice(1); // Remove #
  if (!hash) return 0;
  const index = localizedTabs.findIndex(tab => tab.contentId === hash);
  return index !== -1 ? index : 0;
}

export function TabsComponent({ type, className, localizedTabs }: TabsWidgetFrontendProps) {
  const [activeTabIndex, setActiveTabIndex] = useState(() => {
    // For navigation type, check URL hash for initial tab
    if (type === 'navigations') {
      return getActiveTabIndexFromHash(localizedTabs);
    }
    return 0;
  });

  // Handle browser back/forward navigation for navigation type
  useEffect(() => {
    if (type !== 'navigations') return;

    const handlePopState = () => {
      const newIndex = getActiveTabIndexFromHash(localizedTabs);
      setActiveTabIndex(newIndex);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [type, localizedTabs]);

  // Handle tab change - updates state and URL hash for navigation type
  const handleTabChange = useCallback(
    (_: React.SyntheticEvent, newActiveTabIndex: number) => {
      setActiveTabIndex(newActiveTabIndex);
      if (type === 'navigations') {
        const activeTab = localizedTabs[newActiveTabIndex];
        if (activeTab?.contentId) {
          window.history.pushState(null, '', `#${activeTab.contentId}`);
        }
      }
    },
    [type, localizedTabs],
  );

  // Show/hide tab content based on active tab
  useEffect(() => {
    const activeTab = localizedTabs[activeTabIndex];
    if (!activeTab) {
      // show all tab content elements
      const tabContentElements = document.querySelectorAll('.tab-content');
      tabContentElements.forEach(element => {
        element.removeAttribute('hidden');
        if (element instanceof HTMLElement) {
          element.style.display = '';
        }
      });
      return;
    }

    // Hide all tab content elements
    localizedTabs.forEach(tab => {
      const contentElement = document.getElementById(tab.contentId || '');
      if (contentElement) {
        contentElement.setAttribute('hidden', 'true');
        contentElement.style.display = 'none';
      }
    });

    // Show the active tab content
    const activeContentElement = document.getElementById(activeTab.contentId || '');
    if (activeContentElement) {
      activeContentElement.removeAttribute('hidden');
      activeContentElement.style.display = '';
    }
  }, [activeTabIndex, localizedTabs]);

  return (
    <Tabs
      component={type === 'navigations' ? 'nav' : 'div'}
      value={activeTabIndex}
      onChange={handleTabChange}
      textColor="inherit"
      sx={{
        '& .MuiTab-root.tab-active': {
          color: 'var(--primary)',
        },
      }}
      TabIndicatorProps={{
        sx: {
          backgroundColor: 'var(--primary)',
        },
      }}
      className={className}
    >
      {localizedTabs.map((tab, index) => (
        <Tab
          key={tab.contentId}
          label={tab.label}
          aria-controls={tab.contentId || ''}
          className={classNames({
            tab: type === 'tabs',
            'navigation-title': type === 'navigations',
            'tab-active': index === activeTabIndex,
          })}
        />
      ))}
    </Tabs>
  );
}

export function serializeTabsComponentProps(
  props: TabsWidgetBackendProps,
): TabsWidgetFrontendProps {
  // Get localized tabs - labels come from i18nAttributes
  const localizedTabs =
    props.options.attributes.tabs?.map((tab, index) => ({
      contentId: tab.contentId || '',
      label:
        props.options.i18nAttributes?.[props.context.i18n.locale]?.tabLabels?.[
          tab.contentId || ''
        ] || `Tab ${index + 1}`,
    })) || [];

  return {
    id: props.options.id || props.options.attributes.id || uuid(),
    type: props.options.attributes.type,
    className: props.options.attributes.class || '',
    attributes: props.options.attributes,
    localizedTabs,
  };
}

export function TabsRenderer(props: TabsWidgetBackendProps, document?: Document): string {
  const reactProps = serializeTabsComponentProps(props);
  return renderToString(
    <ReactBootstrap
      component="TabsComponent"
      props={reactProps}
      document={document}
      componentType="tab-container"
    />,
  );
}
